"use client"

import { useState, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import { useNotification } from "../../context/NotificationContext"
import "./UserDetails.css"

const UserDetails = () => {
  const { userId } = useParams()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [farms, setFarms] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editMode, setEditMode] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "user",
    isActive: true,
  })

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch user details
        const userRes = await axios.get(`${API_URL}/api/admin/users/${userId}`)
        setUser(userRes.data)

        // Set form data
        setFormData({
          name: userRes.data.name,
          email: userRes.data.email,
          role: userRes.data.role,
          isActive: userRes.data.isActive,
        })

        // Fetch user profile if exists
        try {
          const profileRes = await axios.get(`${API_URL}/api/admin/users/${userId}/profile`)
          setProfile(profileRes.data)
        } catch (err) {
          console.log("No profile found for user")
        }

        // Fetch user farms
        const farmsRes = await axios.get(`${API_URL}/api/admin/users/${userId}/farms`)
        setFarms(farmsRes.data)

        // Fetch user projects
        const projectsRes = await axios.get(`${API_URL}/api/admin/users/${userId}/projects`)
        setProjects(projectsRes.data)

        setLoading(false)
      } catch (err) {
        console.error(err)
        setError("Failed to load user data")
        setLoading(false)
      }
    }

    fetchUserData()
  }, [userId])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, formData)

      // Update user state
      setUser({
        ...user,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
      })

      setEditMode(false)
      showNotification(t("User updated successfully"), "success")
    } catch (err) {
      console.error(err)
      showNotification(t("Failed to update user"), "error")
    }
  }

  const handleDeleteUser = async () => {
    if (!window.confirm(t("Are you sure you want to delete this user? This action cannot be undone."))) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`)
      showNotification(t("User deleted successfully"), "success")
      navigate("/admin/users")
    } catch (err) {
      console.error(err)
      showNotification(t("Failed to delete user"), "error")
    }
  }

  if (loading) {
    return (
      <div className="user-details-loading">
        <div className="spinner"></div>
        <p>{t("Loading user data...")}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="user-details-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t(error)}</p>
        <Link to="/admin/users" className="back-button">
          <i className="fas fa-arrow-left"></i> {t("Back to Users")}
        </Link>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="user-details-error">
        <i className="fas fa-user-slash"></i>
        <p>{t("User not found")}</p>
        <Link to="/admin/users" className="back-button">
          <i className="fas fa-arrow-left"></i> {t("Back to Users")}
        </Link>
      </div>
    )
  }

  return (
    <div className="user-details-container" dir={direction}>
      <div className="user-details-header">
        <div className="header-left">
          <Link to="/admin/users" className="back-button">
            <i className="fas fa-arrow-left"></i> {t("Back to Users")}
          </Link>
          <h1>{t("User Details")}</h1>
        </div>
        <div className="header-actions">
          {!editMode ? (
            <>
              <button className="edit-button" onClick={() => setEditMode(true)}>
                <i className="fas fa-edit"></i> {t("Edit User")}
              </button>
              <button className="delete-button" onClick={handleDeleteUser}>
                <i className="fas fa-trash-alt"></i> {t("Delete User")}
              </button>
            </>
          ) : (
            <button className="cancel-button" onClick={() => setEditMode(false)}>
              <i className="fas fa-times"></i> {t("Cancel")}
            </button>
          )}
        </div>
      </div>

      <div className="user-details-content">
        <div className="user-details-card">
          <div className="user-info-section">
            <h2>{t("Basic Information")}</h2>

            {editMode ? (
              <form onSubmit={handleSubmit} className="edit-user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t("Name")}</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t("Email")}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="role">{t("Role")}</label>
                    <select id="role" name="role" value={formData.role} onChange={handleChange}>
                      <option value="user">{t("User")}</option>
                      <option value="admin">{t("Admin")}</option>
                    </select>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
                      {t("Active Account")}
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-button">
                    <i className="fas fa-save"></i> {t("Save Changes")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="user-info">
                <div className="info-row">
                  <div className="info-label">{t("Name")}:</div>
                  <div className="info-value">{user.name}</div>
                </div>
                <div className="info-row">
                  <div className="info-label">{t("Email")}:</div>
                  <div className="info-value">{user.email}</div>
                </div>
                <div className="info-row">
                  <div className="info-label">{t("Role")}:</div>
                  <div className="info-value">
                    <span className={`role-badge ${user.role}`}>{user.role === "admin" ? t("Admin") : t("User")}</span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-label">{t("Status")}:</div>
                  <div className="info-value">
                    <span className={`status-badge ${user.isActive ? "active" : "inactive"}`}>
                      {user.isActive ? t("Active") : t("Inactive")}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-label">{t("Joined")}:</div>
                  <div className="info-value">{new Date(user.createdAt).toLocaleDateString()}</div>
                </div>
                <div className="info-row">
                  <div className="info-label">{t("Last Updated")}:</div>
                  <div className="info-value">{new Date(user.updatedAt).toLocaleDateString()}</div>
                </div>
              </div>
            )}
          </div>

          {profile && (
            <div className="user-profile-section">
              <h2>{t("Profile Information")}</h2>
              <div className="profile-info">
                {profile.profilePhoto && (
                  <div className="profile-photo">
                    <img src={`${API_URL}/uploads/profile/${profile.profilePhoto}`} alt={user.name} />
                  </div>
                )}

                <div className="profile-details">
                  {profile.country && (
                    <div className="info-row">
                      <div className="info-label">{t("Country")}:</div>
                      <div className="info-value">{profile.country.name}</div>
                    </div>
                  )}

                  {profile.district && (
                    <div className="info-row">
                      <div className="info-label">{t("District")}:</div>
                      <div className="info-value">{profile.district.name}</div>
                    </div>
                  )}

                  {profile.bio && (
                    <div className="info-row">
                      <div className="info-label">{t("Bio")}:</div>
                      <div className="info-value bio">{profile.bio}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="user-data-section">
          <div className="farms-section">
            <h2>{t("Farms")}</h2>
            {farms.length === 0 ? (
              <p className="no-data">{t("No farms found for this user")}</p>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("Name")}</th>
                      <th>{t("Location")}</th>
                      <th>{t("Area")}</th>
                      <th>{t("Created")}</th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {farms.map((farm) => (
                      <tr key={farm._id}>
                        <td>{farm.name}</td>
                        <td>
                          {farm.district?.name}, {farm.country?.name}
                        </td>
                        <td>
                          {farm.area} {farm.areaUnit}
                        </td>
                        <td>{new Date(farm.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions">
                            <Link to={`/admin/farms/${farm._id}`} className="view-button" title={t("View Farm")}>
                              <i className="fas fa-eye"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="projects-section">
            <h2>{t("Projects")}</h2>
            {projects.length === 0 ? (
              <p className="no-data">{t("No projects found for this user")}</p>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("Name")}</th>
                      <th>{t("Farm")}</th>
                      <th>{t("Crop")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Created")}</th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project._id}>
                        <td>{project.name}</td>
                        <td>{project.farm?.name || t("N/A")}</td>
                        <td>{project.cropDetails?.name || t("N/A")}</td>
                        <td>
                          <span className={`status-badge ${project.status}`}>{t(project.status)}</span>
                        </td>
                        <td>{new Date(project.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions">
                            <Link
                              to={`/admin/projects/${project._id}`}
                              className="view-button"
                              title={t("View Project")}
                            >
                              <i className="fas fa-eye"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDetails
